<script>
import { useCookie } from 'vue-cookie-next'
import { useStore } from 'vuex'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import axios from 'axios'

const COOKIE_KEYS = {
  dismissedAt: 'newsletter_popup_dismissed_at',
  subscribed: 'newsletter_popup_subscribed',
  legacySeen: 'seen_newsletter_popup',
}

const SESSION_SHOWN_KEY = 'newsletter_popup_session_shown'
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000
const POPUP_TRIGGER_DELAY_MS = 12000
const EXCLUDED_PAGE_CLASSES = ['template-product', 'template-collection', 'template-cart', 'template-checkout']
const COOKIE_BANNER_SELECTOR = '#cookie-notification'

export default {
  props: {
    title: { type: String, default: null },
    reward: { type: String, default: null },
  },

  setup(props, ctx) {
    const store = useStore()
    const { setCookie, getCookie } = useCookie()

    const title = ref(props.title)
    const reward = ref(props.reward)

    let hasUserEngaged = false
    let delayElapsed = false
    let triggerTimeout = null
    let consentObserver = null
    const removeEngagementListeners = []

    const getBooleanCookie = (key) => getCookie(key) === 'true'

    const setBooleanCookie = (key, expire = '1y') => {
      setCookie(key, 'true', { expire })
    }

    const setSessionShown = () => {
      try {
        window.sessionStorage.setItem(SESSION_SHOWN_KEY, 'true')
      } catch (error) {
        // Intentionally ignore blocked storage exceptions
      }
    }

    const isSessionShown = () => {
      try {
        return window.sessionStorage.getItem(SESSION_SHOWN_KEY) === 'true'
      } catch (error) {
        return false
      }
    }

    const isElementVisible = (element) => {
      if (!element) return false

      const computed = window.getComputedStyle(element)
      if (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0') return false
      if (element.hasAttribute('hidden')) return false
      if (element.getAttribute('aria-hidden') === 'true') return false

      const rect = element.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return false

      // Treat banners moved fully off-canvas as not visible.
      const inViewport =
        rect.bottom > 0 &&
        rect.top < window.innerHeight &&
        rect.right > 0 &&
        rect.left < window.innerWidth

      return inViewport
    }

    const isCookiePopupClear = () => {
      const consentBanner = document.querySelector(COOKIE_BANNER_SELECTOR)
      if (!consentBanner) return true
      return !isElementVisible(consentBanner)
    }

    const isExcludedPage = () => {
      const bodyClass = document.body?.className || ''
      return EXCLUDED_PAGE_CLASSES.some((className) => bodyClass.includes(className))
    }

    const getDismissedAtTimestamp = () => {
      const timestamp = Number(getCookie(COOKIE_KEYS.dismissedAt))
      return Number.isFinite(timestamp) ? timestamp : 0
    }

    const isPopupQaOverride = () => {
      const params = new URLSearchParams(window.location.search)
      return params.get('popup_test') === '1'
    }

    const getPopupBlockReason = () => {
      if (isPopupQaOverride()) return null
      if (isExcludedPage()) return 'excluded_page'
      if (getBooleanCookie(COOKIE_KEYS.subscribed)) return 'subscribed'
      if (isSessionShown()) return 'shown_in_session'

      const dismissedAt = getDismissedAtTimestamp()
      if (dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return 'dismissed_cooldown'
      if (!isCookiePopupClear()) return 'cookie_popup_visible'

      return null
    }

    const getDebugState = () => {
      const dismissedAt = getDismissedAtTimestamp()
      const dismissedCooldownActive = dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS

      return {
        blockReason: getPopupBlockReason(),
        showNewsletterPopup: store.state.config.showNewsletterPopup,
        excludedPage: isExcludedPage(),
        subscribed: getBooleanCookie(COOKIE_KEYS.subscribed),
        sessionShown: isSessionShown(),
        dismissedAt,
        dismissedCooldownActive,
        cookieBannerClear: isCookiePopupClear(),
        hasUserEngaged,
        delayElapsed,
        popupTriggerDelayMs: POPUP_TRIGGER_DELAY_MS,
        qaOverride: isPopupQaOverride(),
        bodyClass: document.body?.className || '',
      }
    }

    const showPopup = () => {
      if (getPopupBlockReason()) return

      store.commit('config/setNewsletterPopup', true)
      setSessionShown()
    }

    const hidePopup = () => {
      if (!store.state.config.showNewsletterPopup) return

      store.commit('config/setNewsletterPopup', false)
      setCookie(COOKIE_KEYS.dismissedAt, String(Date.now()), { expire: '30d' })
      setBooleanCookie(COOKIE_KEYS.legacySeen)
    }

    const maybeShowPopup = () => {
      if (!delayElapsed || !hasUserEngaged) return
      showPopup()
    }

    const onEngagement = () => {
      hasUserEngaged = true
      maybeShowPopup()
    }

    const addEngagementListener = (eventName) => {
      const handler = () => onEngagement()
      window.addEventListener(eventName, handler, { passive: true })
      removeEngagementListeners.push(() => window.removeEventListener(eventName, handler))
    }

    const startPopupTrigger = () => {
      addEngagementListener('scroll')
      addEngagementListener('pointerdown')
      addEngagementListener('keydown')
      addEngagementListener('touchstart')

      triggerTimeout = window.setTimeout(() => {
        delayElapsed = true
        maybeShowPopup()
      }, POPUP_TRIGGER_DELAY_MS)
    }

    const closePopupIfConsentAppears = () => {
      if (!store.state.config.showNewsletterPopup) return
      if (!isCookiePopupClear()) {
        store.commit('config/setNewsletterPopup', false)
      }
    }

    onMounted(() => {
      if (document.readyState === 'complete') {
        startPopupTrigger()
      } else {
        window.addEventListener('load', startPopupTrigger, { once: true })
      }

      window.__newsletterPopupDebug = getDebugState

      consentObserver = new MutationObserver(closePopupIfConsentAppears)
      consentObserver.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
      })

      window.addEventListener('klaviyoForms', function (e) {
        if (e.detail.type == 'submit') {
          title.value = window.themeVariables.i18n.newsletter.subscribed_title
          setBooleanCookie(COOKIE_KEYS.subscribed)
          setBooleanCookie(COOKIE_KEYS.legacySeen)
          if (reward.value) {
            axios.post('/cart/add.js', { id: reward.value, quantity: 1 })
          }
        }
      })
    })

    onBeforeUnmount(() => {
      if (triggerTimeout) window.clearTimeout(triggerTimeout)
      removeEngagementListeners.forEach((remove) => remove())
      if (consentObserver) consentObserver.disconnect()
      delete window.__newsletterPopupDebug
    })

    return () => ctx.slots.default({ hidePopup, title, reward })
  },
}
</script>
